import React from 'react';
import {StyleSheet, TouchableOpacityProps} from 'react-native';

import {Constants} from '../../constants';
import {useBreakpoints} from '../../lib/hooks/useBreakpoints';
import {colors} from '../../styles';
import {ButtonTheme, Variant} from '../../themes';
import {StyledButtonTheme} from '../../themes';
import {ColorScheme, colorScheme} from '../../util/colorScheme';
import {PressableOpacity} from '../PressableOpacity';
import {Text} from '../Typography';

type AsProps<T extends React.ElementType> = React.ComponentPropsWithoutRef<T>;

export type ButtonProps<T extends React.ElementType = typeof PressableOpacity> =
  TouchableOpacityProps & {
    theme?: ButtonTheme;
    variant?: Variant | 'transparent';
    as?: T;
  } & AsProps<T>;

type ButtonState = 'enabled' | 'disabled';
type ButtonVariant = Variant | 'transparent';

export const buttonThemes: Record<
  ButtonTheme,
  Record<ButtonState, StyledButtonTheme | undefined>
> = {
  black: {
    enabled: {color: colors.black, textColor: colors.white},
    disabled: {
      color: colors.darkGreyMedium33,
      textColor: colors.disabledTextGray,
    },
  },
  white: {
    enabled: {color: colors.white, textColor: colors.black},
    disabled: {
      color: colors.darkGreyMedium33,
      textColor: colors.disabledTextGray,
    },
  },
  gray: {
    enabled: {color: colors.darkGreyMedium, textColor: colors.lightMediumGray},
    disabled: undefined,
  },
  blackOutlined: {
    enabled: {
      color: colors.black,
      textColor: colors.white,
      borderColor: colors.lightMediumGray,
    },
    disabled: {
      color: colors.black,
      textColor: colors.darkGreyMedium33,
      borderColor: colors.darkGreyMedium33,
    },
  },
  whiteOutlined: {
    enabled: {
      color: colors.white,
      textColor: colors.black,
      borderColor: colors.lightMediumGray,
    },
    disabled: undefined,
  },
};

const buttonColorSchemeMap: Record<
  ColorScheme,
  Record<ButtonVariant, ButtonTheme | undefined>
> = {
  dark: {
    primary: 'white',
    secondary: 'blackOutlined',
    transparent: undefined,
    tertiary: undefined,
  },
  light: {
    primary: 'black',
    secondary: 'whiteOutlined',
    transparent: undefined,
    tertiary: undefined,
  },
};

export const buttonColorScheme = buttonColorSchemeMap[colorScheme];

export const Button = <T extends React.ElementType = typeof PressableOpacity>({
  as,
  variant = 'primary',
  theme,
  style,
  disabled,
  children,
  ...rest
}: ButtonProps<T>) => {
  const AsComponent = as ?? PressableOpacity;

  const resolvedTheme = theme ?? buttonColorScheme[variant];
  const themeStyles = resolvedTheme ? buttonThemes[resolvedTheme] : undefined;
  const transparent = variant === 'transparent';
  const state: ButtonState = disabled ? 'disabled' : 'enabled';

  const isDefault = AsComponent === PressableOpacity;

  const {isMedium} = useBreakpoints();

  const renderChildren = () => {
    if (!isDefault) return children;

    return React.Children.map(children, child =>
      typeof child === 'string' || typeof child === 'number' ? (
        <Text
          variant="lead"
          style={[styles.text, {color: themeStyles?.[state]?.textColor}]}>
          {child}
        </Text>
      ) : (
        child
      ),
    );
  };

  return (
    <AsComponent
      style={[
        styles.button,
        {
          height: isMedium ? 46 : 56,
          backgroundColor: themeStyles?.[state]?.color,
          borderColor:
            themeStyles?.[state]?.borderColor ?? themeStyles?.[state]?.color,
        },
        disabled && !themeStyles?.disabled && {opacity: 0.4},
        transparent && styles.transparent,
        style,
      ]}
      {...{disabled}}
      {...rest}>
      {renderChildren()}
    </AsComponent>
  );
};

const styles = StyleSheet.create({
  button: {
    width: '100%',
    borderRadius: 80,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 1,
    gap: 8,
  },
  text: {
    textTransform: 'uppercase',
    // line height leaves more empty space on bottom than on top, so it doesn't look centered
    ...(Constants.IS_WEB && {marginTop: 2}),
  },
  transparent: {
    overflow: 'visible',
    borderWidth: 0,
    height: 'auto',
    width: 'auto',
  },
});
